import { Injectable } from '@angular/core';
import PayazaCheckout from 'payaza-web-sdk';
import { environment } from 'src/environments/environment';
import { Logger } from 'src/services/core/logger/logger.service';
import { Response } from 'src/services/classes/models/response';
import { PaymentInitResponse } from 'src/services/classes/models/payment';
import { NotificationsService } from 'src/services/classes/notifications/notifications.service';
import { OrdersService } from 'src/services/data/orders/orders.service';

const log = new Logger('PaymentService');

export interface PayazaCustomer {
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export interface PayazaHandlers {
  /** Fired ONLY after the backend verify endpoint confirms the payment. */
  onVerified: (res: Response) => void;
  /** Fired when verification fails (server returns error, or the verify call errors). */
  onFailed?: () => void;
  /** Fired when the user dismisses the popup without completing payment. */
  onClose?: () => void;
}

/**
 * Shared Payaza inline-checkout helper used by both the dine-in (collect-email)
 * and takeout (orders) flows. Centralizes the rule that the client callback is
 * never trusted for fulfillment — we always re-verify server-side first.
 */
@Injectable({ providedIn: 'root' })
export class PaymentService {
  constructor(
    private ordersService: OrdersService,
    private notifyService: NotificationsService
  ) {}

  /**
   * Launches the Payaza inline popup using the backend-initialized reference.
   * On the SDK callback we re-verify server-side before calling `onVerified`.
   */
  launchPayaza(
    data: PaymentInitResponse['data'],
    customer: PayazaCustomer,
    handlers: PayazaHandlers
  ): void {
    // The SDK rejects blank first_name / last_name / email and silently blocks the
    // popup from rendering, so fall back to non-blank values derived from the email.
    const email = (customer.email || '').trim();
    const firstName =
      (customer.firstName || '').trim() || this.nameFromEmail(email) || 'Customer';
    const lastName = (customer.lastName || '').trim() || 'Customer';
    const currency = data.currency || 'NGN';

    let checkout: PayazaCheckout;
    try {
      checkout = PayazaCheckout.setup({
        merchant_key: environment.payazaPublicKey,
        connection_mode:
          environment.payazaConnectionMode === 'Live'
            ? PayazaCheckout.LIVE_CONNECTION_MODE
            : PayazaCheckout.TEST_CONNECTION_MODE,
        checkout_amount: Number(data.amount),
        currency,
        currency_code: currency,
        email_address: email,
        first_name: firstName,
        last_name: lastName,
        phone_number: (customer.phoneNumber || '').trim(),
        // Backend's reference — initialized and verified against this exact value. Never client-generated.
        transaction_reference: data.transactionReference,
        onClose: () => handlers.onClose?.(),
        callback: (response: any) => {
        // Only a `success` response means the customer completed payment. Validation
        // errors and failures also arrive here — never trust them for fulfillment.
        const type = (response?.type || '').toString().toLowerCase();
        const succeeded =
          type === 'success' || response?.status === 200 || response?.status === 201;

        if (!succeeded) {
          log.debug('Payaza callback (non-success): ', response);
          this.notifyService.publishMessages(
            response?.data?.message || 'Payment was not completed.',
            'danger',
            1
          );
          handlers.onFailed?.();
          return;
        }

        const ref = response?.data?.transaction_reference || data.transactionReference;
        this.verifyPayment(ref, handlers);
        },
      });

      checkout.showPopup();
    } catch (e) {
      log.debug('Payaza setup/showPopup error: ', e);
      this.notifyService.publishMessages(
        'Unable to open the payment window. Please try again.',
        'danger',
        1
      );
      handlers.onClose?.();
    }
  }

  /** Derives a non-blank first name from an email local part (e.g. "jane.doe@x" -> "Jane"). */
  private nameFromEmail(email: string): string {
    const local = (email.split('@')[0] || '').replace(/[^a-zA-Z]+/g, ' ').trim();
    const first = local.split(' ')[0] || '';
    return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
  }

  /** Server-side verification is the source of truth for fulfillment. */
  private verifyPayment(reference: string, handlers: PayazaHandlers): void {
    this.ordersService.verifyPayment(reference).subscribe(
      (res: Response) => {
        if (res?.status?.toLowerCase() !== 'error') {
          handlers.onVerified(res);
        } else {
          this.notifyService.publishMessages(
            res?.message || 'Payment verification failed.',
            'danger',
            1
          );
          handlers.onFailed?.();
        }
      },
      (error) => {
        log.debug('verify error: ', error);
        this.notifyService.publishMessages(
          'Payment verification failed. Please contact support if you were debited.',
          'danger',
          5
        );
        handlers.onFailed?.();
      }
    );
  }
}
